import { For, Index, Show, createMemo, createSignal, type JSX } from 'solid-js';
import { ArrowDownIcon, ArrowUpIcon } from '~ui/icons';
import { Text } from '~ui/text';

// Plain (non-virtualized) table. Row counts here are portfolios and dividend
// schedules — tens of rows, not tens of thousands — so the DOM cost of
// rendering them all is cheaper than a virtualizer. Header/cell classes match
// the ctcs table so the two apps read the same.

export interface Column<T> {
    id: string;
    header: JSX.Element;
    /**
     * Receives an *accessor*, not a value. Call it inside the returned JSX so
     * the read lands as a leaf effect: the cell's DOM then survives a value
     * change instead of being rebuilt. That is what keeps a focused input in a
     * cell from losing focus and selection on every keystroke.
     */
    cell: (row: () => T, index: number) => JSX.Element;
    /** Relative width; converted to a percentage of the summed widths. */
    size?: number;
    align?: 'left' | 'right';
    /**
     * What this column sorts on. Present makes the header clickable; absent
     * leaves it inert.
     *
     * A separate function rather than reusing `cell` because a cell returns JSX
     * and sorting needs the value behind it — "$1,234.50" and a red-toned N/A
     * do not compare, and the string form of a number sorts 10 before 9.
     */
    sortBy?: (row: T) => number | string | null;
    /** Marks the cell as user-editable. It gets vertical rules on both sides
        running the full cell height, so the columns you can type into read as
        distinct from the derived ones. Controls inside should pass
        `bordered={false}` — the cell owns the rule. */
    editable?: boolean;
}

export interface TableProps<T> {
    columns: Column<T>[];
    rows: T[];
    empty?: JSX.Element;
    /** Rendered as a pinned last row (totals, summary). */
    footer?: JSX.Element;
    /** Off when the table is one of several stacked inside a scrolling pane —
        it then sizes to its rows and the pane owns the scrollbar (and the
        sticky header, which would otherwise collide with the next table's). */
    scroll?: boolean;
    /** Makes rows clickable, for a table that drives a detail panel beside it. */
    onRowClick?: (row: T) => void;
    /** Marks the row that panel is currently showing. */
    isSelected?: (row: T) => boolean;
    class?: string;
}

export function Table<T>(props: TableProps<T>): JSX.Element {
    const scrolls = () => props.scroll !== false;

    const [sort, setSort] = createSignal<{ id: string; dir: 'asc' | 'desc' } | null>(null);

    // First click sorts descending. Every sortable column here is a quantity —
    // yield, assets, exposure — and the question is always which is biggest.
    const toggle = (id: string): void => {
        setSort((current) => (current?.id === id ? { id, dir: current.dir === 'desc' ? 'asc' : 'desc' } : { id, dir: 'desc' }));
    };

    const rows = createMemo(() => {
        const active = sort();
        const column = active ? props.columns.find((c) => c.id === active.id) : undefined;
        if (!active || !column?.sortBy) return props.rows;

        const key = column.sortBy;
        return [...props.rows].sort((a, b) => {
            const left = key(a);
            const right = key(b);
            // Null is not a value, it is the absence of one — an unpriced fund
            // sorts to the bottom either way rather than pretending to be zero
            // and leading an ascending sort.
            if (left == null && right == null) return 0;
            if (left == null) return 1;
            if (right == null) return -1;
            const order = typeof left === 'string' || typeof right === 'string' ? String(left).localeCompare(String(right)) : left - right;
            return active.dir === 'asc' ? order : -order;
        });
    });

    const widthPct = (size?: number) => {
        const total = props.columns.reduce((sum, c) => sum + (c.size ?? 1), 0);
        return `${((size ?? 1) / total) * 100}%`;
    };

    return (
        // The footer is a sibling of the scroll area rather than a <tfoot>, so it
        // sits on the pane's bottom edge whether the rows overflow or leave the
        // pane half empty. `sticky bottom-0` inside the scroller only pins the
        // former case: a short table just ends, and the footer floats mid-pane
        // with dead space under it.
        <div class={`${scrolls() ? 'flex-1 min-h-0 flex flex-col' : ''} ${props.class ?? ''}`}>
            <div class={scrolls() ? 'flex-1 min-h-0 overflow-auto scrollbar-hide' : ''}>
                <Show
                    when={rows().length > 0}
                    fallback={
                        <div class={`flex items-center justify-center ${scrolls() ? 'h-full' : 'h-16'}`}>
                            <Text tone='muted'>{props.empty ?? 'No data'}</Text>
                        </div>
                    }>
                    <table class='w-full border-collapse table-fixed'>
                        <thead class={`bg-background ${scrolls() ? 'sticky top-0 z-10' : ''}`}>
                            <tr>
                                <For each={props.columns}>
                                    {/* h-row sits on the cell, not on a child: with
                                        border-collapse the header's bottom border is shared
                                        with the first body row and lands as a half-pixel on
                                        top of whatever the child is, so a fixed-height child
                                        makes the header row 29.5px against everything else's
                                        29. border-box on the cell absorbs it. Padding matches
                                        the body cell below so header labels sit on the same x
                                        as the values — including the first/last column
                                        insets, which have to be on the cell since the child
                                        is always both first and last. */}
                                    {(col) => (
                                        <th
                                            class={`text-sm font-normal text-foreground h-row px-1 first:pl-2 last:pr-2 border-b bg-background overflow-hidden ${col.align === 'right' ? 'text-right' : 'text-left'} ${col.editable ? 'border-x' : ''}`}
                                            style={{ width: widthPct(col.size) }}>
                                            {/* Clickable only where there is something
                                                to sort by, so an inert header does not
                                                offer an affordance it will not honour. */}
                                            <div
                                                class={`flex h-full items-center gap-1 min-w-0 ${col.align === 'right' ? 'justify-end' : ''} ${col.sortBy ? 'cursor-default hover:bg-muted' : ''}`}
                                                onClick={col.sortBy ? () => toggle(col.id) : undefined}>
                                                <Text tone={sort()?.id === col.id ? 'foreground' : 'muted'} class='truncate min-w-0'>
                                                    {col.header}
                                                </Text>
                                                <Show when={sort()?.id === col.id}>
                                                    <span class='shrink-0'>{sort()!.dir === 'asc' ? <ArrowUpIcon /> : <ArrowDownIcon />}</span>
                                                </Show>
                                            </div>
                                        </th>
                                    )}
                                </For>
                            </tr>
                        </thead>
                        {/* Index, not For: rows are positional data, and Index keeps
                            the <tr> DOM in place and pushes new values through a
                            signal instead of re-creating the row. For would key on
                            object identity, and the rows are derived objects that
                            are freshly built on every read. */}
                        <tbody>
                            <Index each={rows()}>
                                {/* The last row drops its rule: with border-collapse an
                                    interior row shares its border with the row below and
                                    absorbs it, but the final one has nothing to share with,
                                    so half a pixel hangs outside the row box and makes the
                                    table 0.5px taller than its rows — enough to land
                                    everything after it on half-pixel boundaries. A footer
                                    supplies its own top rule, and a table that ends a pane is
                                    closed off by the pane seam. */}
                                {(row, i) => (
                                    // The selected fill is the same one the Select
                                    // menu uses for its chosen option, so "this is
                                    // the row you are looking at" reads the same
                                    // wherever it appears. Hover still wins over it,
                                    // which is the right way round — the pointer
                                    // should always show what it is about to pick.
                                    <tr
                                        class={`border-b border-muted last:border-b-0 cursor-default hover:bg-muted ${props.isSelected?.(row()) ? 'bg-muted-foreground/20' : ''}`}
                                        onClick={props.onRowClick ? () => props.onRowClick!(row()) : undefined}>
                                        <For each={props.columns}>
                                            {(col) => (
                                                <td class={`px-1 first:pl-2 last:pr-2 h-row text-sm whitespace-nowrap overflow-hidden ${col.align === 'right' ? 'text-right' : 'text-left'} ${col.editable ? 'border-x' : ''}`}>{col.cell(row, i)}</td>
                                            )}
                                        </For>
                                    </tr>
                                )}
                            </Index>
                        </tbody>
                    </table>
                </Show>
            </div>

            {/* Grid rather than flex, so an arbitrary footer element stretches to
                the full width instead of shrinking to its content — every caller
                passes a justify-between row that depends on it. */}
            <Show when={props.footer && rows().length > 0}>
                <div class='grid h-row shrink-0 items-center border-t bg-background px-2'>{props.footer}</div>
            </Show>
        </div>
    );
}
